import { useState, useEffect, useCallback } from "react";
import { AuthContext } from "../contexts/AuthContext";
import { authAPI } from "../models";
import { getToken, setToken, removeToken } from "../services/authService";

/**
 * Auth Provider
 * Manages authentication state and provides auth methods to the app
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setTokenState] = useState(getToken());
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  /**
   * Check authentication status on mount and token change
   */
  const checkAuth = useCallback(async () => {
    const currentToken = getToken();

    if (!currentToken) {
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
      return;
    }

    try {
      const response = await authAPI.getCurrentUser();
      setUser(response.data.user);
      setIsAuthenticated(true);
    } catch (error) {
      console.error("Auth check failed:", error);
      // Token is invalid, remove it
      removeToken();
      setUser(null);
      setIsAuthenticated(false);
      setTokenState(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check auth on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Listen for OAuth callback and token changes
  useEffect(() => {
    const handleAuthChange = () => {
      checkAuth();
    };

    // Listen for custom auth-change event (triggered by OAuth callback)
    window.addEventListener("auth-change", handleAuthChange);

    return () => {
      window.removeEventListener("auth-change", handleAuthChange);
    };
  }, [checkAuth]);

  /**
   * Login user
   */
  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);
      const { user: userData, token: newToken } = response.data;

      setToken(newToken);
      setUser(userData);
      setTokenState(newToken);
      setIsAuthenticated(true);

      return { success: true, user: userData };
    } catch (error) {
      console.error("Login failed:", error);
      const errorMessage =
        error.response?.data?.message || error.message || "Login failed";
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Register new user
   */
  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      const { user: newUser, token: newToken } = response.data;

      setToken(newToken);
      setUser(newUser);
      setTokenState(newToken);
      setIsAuthenticated(true);

      return { success: true, user: newUser };
    } catch (error) {
      console.error("Registration failed:", error);
      const errorMessage =
        error.response?.data?.message || error.message || "Registration failed";
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Logout user
   */
  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error("Logout error:", error);
      // Continue with logout even if API call fails
    } finally {
      setUser(null);
      setTokenState(null);
      setIsAuthenticated(false);
      removeToken();
    }
  };

  /**
   * Update user profile
   */
  const updateProfile = async (profileData) => {
    try {
      const response = await authAPI.updateProfile(profileData);
      const updatedUser = response.data.user;

      // Note: JWT token still has old data
      // User needs to re-login to get updated data in token
      setUser(updatedUser);

      return { success: true, user: updatedUser, note: response.note };
    } catch (error) {
      console.error("Profile update failed:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Profile update failed";
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Change password
   */
  const changePassword = async (currentPassword, newPassword) => {
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      return { success: true };
    } catch (error) {
      console.error("Password change failed:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Password change failed";
      return { success: false, error: errorMessage };
    }
  };

  const value = {
    user,
    token,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
